import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { MdArrowBackIos, MdArrowForwardIos } from "react-icons/md";
import Select from "react-select";
import { useLocation } from "react-router";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PERIOD_SELECT_STYLES = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "36px",
    height: "36px",
    backgroundColor: "white",
    borderColor: state.isFocused ? "#63b3ed" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #63b3ed" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#63b3ed" : "#9ca3af",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "34px",
    padding: "0 8px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "34px",
  }),
  option: (provided) => ({
    ...provided,
    color: "#111827",
    padding: "8px 12px",
    fontSize: "14px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "14px",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 10010,
  }),
};

const formatToolbarDate = (date, includeYear = true) => {
  if (!date) return "";
  const formatted = moment(date);
  return includeYear ? formatted.format("D MMM YYYY") : formatted.format("D MMM");
};

const buildToolbarDateDisplayText = (startDate, endDate, period) => {
  if (!startDate) return "";
  if (period === "daily") {
    return formatToolbarDate(startDate, true);
  }
  if (!endDate) {
    return formatToolbarDate(startDate, true);
  }
  const start = moment(startDate);
  const end = moment(endDate);
  if (start.isSame(end, "day")) {
    return formatToolbarDate(startDate, true);
  }
  if (start.year() === end.year()) {
    return `${formatToolbarDate(startDate, false)} - ${formatToolbarDate(endDate, true)}`;
  }
  return `${formatToolbarDate(startDate, true)} - ${formatToolbarDate(endDate, true)}`;
};

const NewDateRangeSelect = (props) => {
  const {
    dateHandler,
    isDisabled = false,
    onlyView = false,
    onChangeView,
    defaultType,
    defaultFortnightView,
    enableDailyView = false,
  } = props;

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const location = useLocation();
  const calendarRef = useRef();
  const dateButtonRef = useRef();
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const [selectedOption, setSelectedOption] = useState();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const lastDateNotifyRef = useRef("");

  const handleCustomDateSelect = (date) => {
    if (selectedOption?.value === "daily") {
      const selectedDay = moment(date).startOf("day");
      setStartDate(selectedDay);
      setEndDate(selectedDay);
      setIsCalendarOpen(false);
      return;
    }

    const selectedMonday = moment(date).startOf("isoWeek");
    let fortnightEnd;
    if (selectedOption.value === "weekly") {
      fortnightEnd = moment(selectedMonday).add(6, "days");
    } else {
      fortnightEnd = moment(selectedMonday).add(13, "days");
    }

    setStartDate(selectedMonday);
    setEndDate(fortnightEnd);
    setIsCalendarOpen(false);
  };
  //TO CLOSE THE CALENDAR IF CLICKS OUTSIDE
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current?.contains(event.target)) return;
      if (dateButtonRef.current?.contains(event.target)) return;
      setIsCalendarOpen(false);
    };

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCalendarOpen]);

  const updateCalendarPosition = () => {
    if (!dateButtonRef.current) return;
    const rect = dateButtonRef.current.getBoundingClientRect();
    setCalendarPosition({ top: rect.bottom + 4, left: rect.left });
  };

  useEffect(() => {
    if (!isCalendarOpen) return;
    updateCalendarPosition();
    const handleReposition = () => updateCalendarPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isCalendarOpen]);

  const options = useMemo(() => {
    const base = [
      {
        value: "fortnightly",
        label: "Fortnightly",
      },
      {
        value: "weekly",
        label: "Weekly",
      },
    ];

    if (enableDailyView) {
      base.push({
        value: "daily",
        label: `Daily`,
      });
    }

    return base;
  }, [enableDailyView]);

  // Helper function to calculate current/previous fortnight using reference dates
  const calculateFortnightDates = () => {
    const referenceMondayCurrent = moment("2025-01-06");
    const referenceMondayPrevious = moment("2025-01-13");
    const today = moment();

    const daysSinceRefCurrent = today.diff(referenceMondayCurrent, "days");
    const fortnightsSinceRefCurrent = Math.floor(daysSinceRefCurrent / 14);

    const daysSinceRefPrevious = today.diff(referenceMondayPrevious, "days");
    const fortnightsSinceRefPrevious = Math.floor(daysSinceRefPrevious / 14);

    const currentMonday = moment(referenceMondayCurrent).add(
      fortnightsSinceRefCurrent * 14,
      "days"
    );

    const previousMonday = moment(referenceMondayPrevious).add(
      fortnightsSinceRefPrevious * 14,
      "days"
    );

    return { currentMonday, previousMonday };
  };

  useEffect(() => {
    if (!selectedOption) return;

    if (selectedOption.value === "daily") {
      return;
    }

    if (selectedOption.value === "weekly") {
      // Weekly always uses current week (this Monday); never depends on fortnight setting
      const weekStart = moment().startOf("isoWeek");
      setStartDate(weekStart);
      setEndDate(moment(weekStart).add(6, "days"));
      return;
    }

    // Fortnightly: depends on defaultFortnightView (f1/f2)
    if (!defaultFortnightView) return;
    const { currentMonday, previousMonday } = calculateFortnightDates();
    if (defaultFortnightView === "f1") {
      setStartDate(currentMonday);
      setEndDate(moment(currentMonday).add(13, "days"));
    } else if (defaultFortnightView === "f2") {
      setStartDate(previousMonday);
      setEndDate(moment(previousMonday).add(13, "days"));
    }
  }, [selectedOption, defaultFortnightView]);

  useEffect(() => {
    if (defaultType) {
      const selectedValue = options?.filter(
        (item) => item?.value === defaultType
      );

      setSelectedOption(selectedValue[0]);
    }
  }, [defaultType, options]);

  useEffect(() => {
    if (selectedOption?.value === "daily" && !startDate) {
      const today = moment().startOf("day");
      setStartDate(today);
      setEndDate(today);
    }
  }, [selectedOption, startDate]);

  useEffect(() => {
    if (!startDate || !endDate || !dateHandler) return;

    const notifyKey = `${selectedOption?.value || ""}|${moment(startDate).format("YYYY-MM-DD")}|${moment(endDate).format("YYYY-MM-DD")}`;
    if (lastDateNotifyRef.current === notifyKey) return;
    lastDateNotifyRef.current = notifyKey;

    dateHandler({ startDate, endDate, view: selectedOption?.value });
  }, [startDate, endDate, selectedOption?.value, dateHandler]);

  const handleDateChange = (value) => {
    if (!selectedOption?.value) return;

    let startOfWeek = null;
    let endOfWeek = null;

    if (selectedOption.value === "daily") {
      if (value === "back") {
        startOfWeek = moment(startDate).subtract(1, "days");
        endOfWeek = moment(startDate).subtract(1, "days");
      } else if (value === "forward") {
        startOfWeek = moment(startDate).add(1, "days");
        endOfWeek = moment(startDate).add(1, "days");
      }
    } else if (value === "back") {
      if (selectedOption.value === "weekly") {
        startOfWeek = moment(startDate).subtract(7, "days");
        endOfWeek = moment(endDate).subtract(7, "days");
      } else {
        startOfWeek = moment(startDate).subtract(14, "days");
        endOfWeek = moment(endDate).subtract(14, "days");
      }
    } else if (value === "forward") {
      if (selectedOption.value === "weekly") {
        startOfWeek = moment(startDate).add(7, "days");
        endOfWeek = moment(endDate).add(7, "days");
      } else {
        startOfWeek = moment(startDate).add(14, "days");
        endOfWeek = moment(endDate).add(14, "days");
      }
    }

    if (startOfWeek && endOfWeek) {
      setStartDate(startOfWeek);
      setEndDate(endOfWeek);
    }
  };

  const handleOptionChange = (option) => {
    setSelectedOption(option);

    let currentMonday, previousMonday;

    if (option.value === "fortnightly") {
      const fortnightDates = calculateFortnightDates();
      currentMonday = fortnightDates.currentMonday;
      previousMonday = fortnightDates.previousMonday;
    } else {
      // Weekly mode uses current week's Monday
      currentMonday = moment().startOf("isoWeek");
      previousMonday = moment(currentMonday).subtract(7, "days");
    }

    if (option.value === "fortnightly") {
      if (defaultFortnightView === "f1") {
        setStartDate(currentMonday);
        setEndDate(moment(currentMonday).add(13, "days"));
      } else if (defaultFortnightView === "f2") {
        setStartDate(previousMonday);
        setEndDate(moment(previousMonday).add(13, "days"));
      }
    }

    if (option.value === "weekly") {
      // Weekly always uses current week Monday; ignore fortnight setting
      setStartDate(currentMonday);
      setEndDate(moment(currentMonday).add(6, "days"));
    }

    if (option.value === "daily") {
      const today = moment().startOf("day");
      setStartDate(today);
      setEndDate(today);
    }

    if (onlyView && onChangeView) {
      onChangeView(option);
    }
  };

  const handleJumpCurrentWeekForthnight = () => {
    let currentMonday, previousMonday;

    if (selectedOption.value === "fortnightly") {
      const fortnightDates = calculateFortnightDates();
      currentMonday = fortnightDates.currentMonday;
      previousMonday = fortnightDates.previousMonday;
    } else {
      // Weekly mode uses current week's Monday
      currentMonday = moment().startOf("isoWeek");
      previousMonday = moment(currentMonday).subtract(7, "days");
    }

    let fortnightEnd;
    if (selectedOption.value === "daily") {
      const today = moment().startOf("day");
      setStartDate(today);
      setEndDate(today);
    } else if (selectedOption.value === "weekly") {
      // Weekly always jumps to current week; ignore fortnight setting
      fortnightEnd = moment(currentMonday).add(6, "days");
      setStartDate(currentMonday);
      setEndDate(fortnightEnd);
    } else {
      if (defaultFortnightView === "f1") {
        fortnightEnd = moment(currentMonday).add(13, "days");
        setStartDate(currentMonday);
        setEndDate(fortnightEnd);
      } else if (defaultFortnightView === "f2") {
        fortnightEnd = moment(previousMonday).add(13, "days");
        setStartDate(previousMonday);
        setEndDate(fortnightEnd);
      }
    }

    setIsCalendarOpen(false);
  };

  if (onlyView) {
    return (
      <Select
        className={` ${
          location.pathname === "/rostering/schedule" ? "z-20" : "z-9"
        }`}
        value={selectedOption}
        onChange={(option) => handleOptionChange(option)}
        options={options}
        styles={PERIOD_SELECT_STYLES}
        menuPortalTarget={document.body}
      />
    );
  }

  const dateLabel =
    selectedOption?.value === "daily" ? "Select Date" : "Select Date Range";

  const dateDisplayText = buildToolbarDateDisplayText(
    startDate,
    endDate,
    selectedOption?.value
  );

  const canNavigate = startDate && endDate && !isDisabled;

  return (
    <div className='schedule-date-range flex shrink-0 flex-nowrap items-end gap-x-3'>
      <div className='flex shrink-0 flex-col'>
        <label className='mb-1 block text-xs font-medium leading-4 text-gray-600'>
          Choose Period
        </label>
        <Select
          className={`min-w-[132px] max-w-[132px] whitespace-nowrap ${
            location.pathname === "/rostering/schedule" ? "z-[999]" : "z-9"
          }`}
          value={selectedOption}
          onChange={(option) => handleOptionChange(option)}
          options={options}
          isDisabled={isDisabled}
          styles={PERIOD_SELECT_STYLES}
          menuPortalTarget={document.body}
        />
      </div>

      <div className='flex shrink-0 flex-col'>
        <label className='mb-1 block whitespace-nowrap text-xs font-medium leading-4 text-gray-600'>
          {dateLabel}
        </label>
        <div className='flex flex-nowrap items-stretch gap-1.5'>
          <button
            type='button'
            disabled={!canNavigate}
            onClick={() => canNavigate && handleDateChange("back")}
            aria-label='Previous period'
            className='flex h-9 w-7 shrink-0 items-center justify-center rounded-md background-gradient border-gradient text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40'
          >
            <MdArrowBackIos className='ml-0.5 h-3 w-3' />
          </button>

          <div className='relative w-[160px] shrink-0'>
            <button
              ref={dateButtonRef}
              type='button'
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  updateCalendarPosition();
                  setIsCalendarOpen(true);
                }
              }}
              className='schedule-date-display flex h-9 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 text-center text-xs text-gray-900 shadow-sm transition-colors hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <span className='truncate whitespace-nowrap'>{dateDisplayText}</span>
            </button>
            {isCalendarOpen &&
              createPortal(
                <div
                  ref={calendarRef}
                  className='schedule-datepicker-popover fixed z-[10010] w-fit rounded-lg border border-gray-200 bg-white shadow-lg'
                  style={{
                    top: calendarPosition.top,
                    left: calendarPosition.left,
                  }}
                >
                  <DatePicker
                    calendarStartDay={1}
                    selected={startDate ? new Date(startDate) : new Date()}
                    onChange={handleCustomDateSelect}
                    inline
                  >
                    <div className='flex items-center justify-center px-2 pb-2'>
                      <button
                        type='button'
                        className='cursor-pointer rounded-lg border border-gray-200 bg-[#F3F2FB] px-3 py-1 text-sm text-[#3D1CBA] hover:bg-[#ebe9f8]'
                        onClick={handleJumpCurrentWeekForthnight}
                      >
                        Jump to Current{" "}
                        {selectedOption.value === "daily"
                          ? "Day"
                          : selectedOption.value === "weekly"
                            ? "Week"
                            : "Fortnight"}
                      </button>
                    </div>
                  </DatePicker>
                </div>,
                document.body
              )}
          </div>

          <button
            type='button'
            disabled={!canNavigate}
            onClick={() => canNavigate && handleDateChange("forward")}
            aria-label='Next period'
            className='flex h-9 w-7 shrink-0 items-center justify-center rounded-md background-gradient border-gradient text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40'
          >
            <MdArrowForwardIos className='h-3 w-3' />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewDateRangeSelect;
